/**
 * ScrollToBottomButton Component
 *
 * Floating button that appears when user scrolls up, allowing quick return to bottom.
 * Uses a simplified version of Vercel's scroll detection logic.
 *
 * Extracted from:
 * - vercel-ai-chatbot/components/messages.tsx (lines 105-114)
 * - vercel-ai-chatbot/hooks/use-scroll-to-bottom.tsx (simplified)
 */

import { useEffect, useState, useCallback } from 'react';

/**
 * ArrowDown Icon
 * Simple chevron pointing down
 */
const ArrowDownIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 14l-7 7m0 0l-7-7m7 7V3"
    />
  </svg>
);

interface ScrollToBottomButtonProps {
  /** Ref to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Ref to the bottom element */
  endRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to detect if user is scrolled to bottom
 *
 * Returns:
 * - isAtBottom: boolean - true if within 100px of bottom
 * - scrollToBottom: function - scroll to bottom with smooth animation
 */
export function useScrollDetection(
  containerRef: React.RefObject<HTMLDivElement>
) {
  const [isAtBottom, setIsAtBottom] = useState(true);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    // Consider "at bottom" if within 100px (Vercel pattern)
    const atBottom = scrollTop + clientHeight >= scrollHeight - 100;
    setIsAtBottom(atBottom);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Listen to scroll events
    container.addEventListener('scroll', handleScroll);

    // Check initial state
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, handleScroll]);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      if (!containerRef.current) return;

      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior,
      });
    },
    [containerRef]
  );

  return { isAtBottom, scrollToBottom };
}

/**
 * ScrollToBottomButton Component
 *
 * Renders a floating button that appears when user scrolls up.
 * Clicking the button smoothly scrolls back to the bottom.
 *
 * Features:
 * - Only visible when not at bottom
 * - Centered horizontally, fixed position
 * - Smooth scroll animation
 * - Hover effect
 *
 * Usage:
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const endRef = useRef<HTMLDivElement>(null);
 *
 * <div ref={containerRef} className="overflow-y-auto">
 *   {messages}
 *   <div ref={endRef} />
 * </div>
 *
 * <ScrollToBottomButton containerRef={containerRef} endRef={endRef} />
 * ```
 */
export const ScrollToBottomButton = ({
  containerRef,
  endRef,
}: ScrollToBottomButtonProps) => {
  const { isAtBottom, scrollToBottom } = useScrollDetection(containerRef);

  // Don't render if at bottom
  if (isAtBottom) return null;

  return (
    // `position: sticky` instead of `absolute` — `absolute` was
    // re-anchoring to whichever `position: relative` ancestor it found
    // first (often a recharts widget inside an assistant message),
    // which made the button hover over chart bars instead of the
    // scroll viewport. `sticky` keeps the button in the messages
    // flex-column flow but pins it to the bottom of the visible
    // scroll viewport via `bottom: 16px`. `self-center` centres it
    // horizontally inside the flex-column parent without needing
    // `left: 50% + translateX(-50%)`. `mt-auto` is a safety net
    // (pushes it to the column's bottom when content is short).
    <button
      onClick={() => scrollToBottom('smooth')}
      className="sticky bottom-4 z-50 self-center mt-auto rounded-full p-1.5 transition-all shrink-0"
      style={{
        background: 'var(--d-ink)',
        color: '#F6F2EE',
        border: '1px solid rgba(28,26,22,0.15)',
        boxShadow:
          '0 8px 20px -8px rgba(28,26,22,0.30), 0 2px 6px -2px rgba(28,26,22,0.20)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--d-accent-deep)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--d-ink)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      aria-label="Scroll to bottom"
      type="button"
    >
      <ArrowDownIcon className="w-3.5 h-3.5" />
    </button>
  );
};
